import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { useSale } from 'root/apps/QuoteMachineIntegration/hooks/useSale';
import DepositService from 'root/apps/QuoteMachineIntegration/services/DepositService';
import { DepositPaymentModalProps } from 'root/apps/QuoteMachineIntegration/state/DepositPaymentModalProps';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import { datadogRum } from 'root/datadog/datadogRum';

export const errorMessages = {
  FETCH_ERROR: 'There was an error fetching the deposits for this customer.',
};

function DepositPaymentModal({
  transactionId,
  balance,
  defaultValue,
  onCancel,
  onSubmit,
  onError,
}: DepositPaymentModalProps) {
  const { sale } = useSale(transactionId);
  const {
    data: deposits,
    isLoading: loadingDeposits,
    isError: isErrorLoadingDeposits,
  } = useQuery({
    queryKey: ['fetchDeposits', sale],
    queryFn: () =>
      DepositService.fetchAll(
        sale?.Customer.customerID ?? '',
        sale?.Customer.creditAccountID ?? ''
      ),
    enabled: !!sale,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    window.showNotifier('working');
    window.hideNotifier('warning');
  }, []);

  if (isErrorLoadingDeposits) {
    window.hideNotifier('working');
    onError(errorMessages.FETCH_ERROR);
    return <></>;
  }

  if (sale && deposits && !loadingDeposits) {
    window.hideNotifier('working');
    return (
      <Parcel
        key="deposit-payment-modal"
        mountParcel={mountRootParcel}
        baseLocation="/register.php"
        skipCSSUnmount={true}
        sessionData={getSessionData(sale)}
        deposits={deposits.data}
        balance={balance}
        defaultValue={defaultValue}
        onCancel={onCancel}
        onSubmit={onSubmit}
        config={async () =>
          (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
            .DepositPaymentModalConfig
        }
        handleError={(err: Error) => datadogRum.addError(err)}
      />
    );
  }

  return <></>;
}

export const renderDepositPaymentModal = (props: DepositPaymentModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: DepositPaymentModal,
    selector: '[data-react-root="ReactModal"]',
  });

  RenderedComponent.mount({
    props: { ...props },
  });
};
